/* Bannière de vérification e-mail — enrichie pour la bêta.
 *
 * Objectifs :
 *   - Afficher clairement à l'utilisateur l'adresse à laquelle le mail a été envoyé
 *   - Rappeler de vérifier le dossier « spam »
 *   - Bouton « Renvoyer » avec cooldown 60s (protège l'infrastructure email)
 *   - Lien « Corriger l'adresse » vers Paramètres → Mon compte
 *   - Statut visuel : à vérifier (jaune) / renvoi en cours / renvoi ok / renvoi échec
 *   - Masqué automatiquement quand l'onboarding wizard bloquant est ouvert
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Mail, X, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SESSION_KEY = "fonbex_email_banner_dismissed";
const LAST_SENT_KEY = "fonbex_email_last_sent";
const COOLDOWN_MS = 60_000; // 60 s

export default function EmailVerifyBanner() {
  const { user, organizations, activeOrgId } = useAuth();
  const activeOrg = organizations?.find((o) => o.id === activeOrgId);
  // Masquer si l'onboarding wizard est ouvert (évite l'overlap mobile).
  const onboardingOpen =
    activeOrg &&
    !activeOrg.is_demo &&
    !activeOrg.onboarding_completed &&
    sessionStorage.getItem("fonbex_onboarding_skipped_org") !== activeOrg.id;

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "1",
  );
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(() => {
    const v = Number(localStorage.getItem(LAST_SENT_KEY) || 0);
    return Number.isFinite(v) ? v : 0;
  });
  const [now, setNow] = useState(Date.now());
  const [status, setStatus] = useState("pending"); // pending | resent | failed

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!user || user.email_verified || dismissed || onboardingOpen) return null;

  const remaining = Math.max(0, COOLDOWN_MS - (now - lastSent));
  const canResend = remaining <= 0 && !sending;

  const resend = async () => {
    if (!canResend) return;
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      const nowMs = Date.now();
      setLastSent(nowMs);
      localStorage.setItem(LAST_SENT_KEY, String(nowMs));
      setStatus("resent");
      toast.success(`E-mail renvoyé à ${user.email} — pensez à vérifier vos spams.`);
    } catch (err) {
      console.error("Resend verification failed:", err);
      setStatus("failed");
      toast.error("Envoi impossible. Réessayez dans quelques instants.");
    } finally {
      setSending(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setDismissed(true);
  };

  const StatusIcon = status === "resent" ? CheckCircle2 : status === "failed" ? AlertTriangle : Mail;
  const bgClass =
    status === "resent"
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : status === "failed"
        ? "bg-red-50 border-red-200 text-red-900"
        : "bg-amber-50 border-amber-200 text-amber-900";

  return (
    <div
      data-testid="email-verify-banner"
      className={`border-b px-4 sm:px-6 py-2.5 ${bgClass}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <StatusIcon className="h-4 w-4 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1 min-w-0">
            {status === "resent" ? (
              <span>
                <strong>E-mail renvoyé</strong> à <strong className="font-mono">{user.email}</strong>.
                <span className="ml-1 opacity-80">Pensez à consulter vos spams.</span>
              </span>
            ) : status === "failed" ? (
              <span>
                <strong>Envoi impossible.</strong>{" "}
                <span className="opacity-80">Vérifiez votre adresse ou réessayez sous une minute.</span>
              </span>
            ) : (
              <span>
                Vérifiez votre e-mail — nous avons envoyé un lien à{" "}
                <strong className="font-mono break-all">{user.email}</strong>.{" "}
                <span className="opacity-80">Consultez votre boîte de réception <em>et vos spams</em>.</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={resend}
            disabled={!canResend}
            data-testid="email-verify-resend"
            className="underline font-medium text-xs sm:text-sm hover:opacity-80 disabled:opacity-40 disabled:no-underline whitespace-nowrap"
          >
            {sending ? (
              <span className="inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Envoi…
              </span>
            ) : remaining > 0 ? (
              `Renvoi possible dans ${Math.ceil(remaining / 1000)} s`
            ) : (
              "Renvoyer l'e-mail"
            )}
          </button>
          <Link
            to="/app/settings"
            data-testid="email-verify-edit"
            className="underline text-xs sm:text-sm hover:opacity-80 whitespace-nowrap"
            title="Modifier l'adresse depuis votre compte"
          >
            Corriger l&apos;adresse
          </Link>
          <button
            onClick={dismiss}
            className="opacity-70 hover:opacity-100"
            data-testid="email-verify-dismiss"
            aria-label="Masquer pour cette session"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
